"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "@garagem/ui/lib/utils"

/**
 * A title or description rendered inside a Dialog. `id` is set only when the
 * caller gave the part an id of its own; otherwise the part keeps the id
 * Radix generated, which the content already references.
 */
type DialogPart = { id: string | undefined } | null
type SetDialogPart = React.Dispatch<React.SetStateAction<DialogPart>>
type DialogNaming = {
  title: DialogPart
  description: DialogPart
  setTitle: SetDialogPart
  setDescription: SetDialogPart
}

const DialogNamingContext = React.createContext<DialogNaming | null>(null)

/** Tells the enclosing Dialog that this part is rendered, and under which id. */
function useDialogPart(set: SetDialogPart | undefined, id: string | undefined) {
  React.useLayoutEffect(() => {
    if (!set) return
    const part = { id }
    set(part)
    return () => set((current) => (current === part ? null : current))
  }, [set, id])
}

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const [title, setTitle] = React.useState<DialogPart>(null)
  const [description, setDescription] = React.useState<DialogPart>(null)
  const naming = React.useMemo(
    () => ({ title, description, setTitle, setDescription }),
    [title, description],
  )
  return (
    <DialogNamingContext.Provider value={naming}>
      <DialogPrimitive.Root data-slot="dialog" {...props} />
    </DialogNamingContext.Provider>
  )
}

/**
 * The accessible name and description of the content. A `DialogTitle` names
 * the dialog and a `DialogDescription` describes it; `aria-labelledby` and
 * `aria-describedby` passed to the content win over both. Without a title the
 * content gets a hidden one: the `aria-label` when there is one, else "Dialog".
 */
function useDialogNaming(props: {
  "aria-label"?: string | undefined
  "aria-labelledby"?: string | undefined
  "aria-describedby"?: string | undefined
}) {
  const naming = React.useContext(DialogNamingContext)
  const aria: {
    "aria-labelledby"?: string | undefined
    "aria-describedby"?: string | undefined
  } = {}
  if (props["aria-labelledby"] === undefined && naming?.title?.id)
    aria["aria-labelledby"] = naming.title.id
  if (naming && !("aria-describedby" in props)) {
    // Radix always references its description id, even when nothing has it.
    if (!naming.description) aria["aria-describedby"] = undefined
    else if (naming.description.id)
      aria["aria-describedby"] = naming.description.id
  }
  return {
    aria,
    fallbackTitle: naming?.title ? null : (props["aria-label"] ?? "Dialog"),
  }
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  )
})
DialogOverlay.displayName = "DialogOverlay"

function DialogContent({
  className,
  children,
  closeLabel = "Close",
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  closeLabel?: string
}) {
  const { aria, fallbackTitle } = useDialogNaming(props)
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-background data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
          "flex max-h-[calc(100vh-8rem)] flex-col",
          className,
        )}
        {...props}
        {...aria}
      >
        {fallbackTitle !== null && (
          <DialogPrimitive.Title className="sr-only">
            {fallbackTitle}
          </DialogPrimitive.Title>
        )}
        {children}
        <DialogPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
          <XIcon className="size-4" />
          <span className="sr-only">{closeLabel}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex flex-col gap-2 text-center sm:text-left",
        "flex-none",
        className,
      )}
      {...props}
    />
  )
}

function DialogContainer({ className, ...props }: React.ComponentProps<"div">) {
  const [hasScroll, setHasScroll] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const checkOverflow = () => {
      setHasScroll(container.scrollHeight > container.clientHeight)
    }

    checkOverflow()

    const resizeObserver = new ResizeObserver(checkOverflow)
    resizeObserver.observe(container)
    return () => {
      resizeObserver.disconnect()
      setHasScroll(false)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      data-slot="dialog-container"
      data-overflow={hasScroll}
      className={cn(
        "flex-1 min-h-0 overflow-y-auto",
        "data-[overflow=true]:pr-2 data-[overflow=true]:sm:pr-4",
        className,
      )}
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        "flex-none",
        className,
      )}
      {...props}
    />
  )
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, id, ...props }, ref) => {
  useDialogPart(React.useContext(DialogNamingContext)?.setTitle, id)

  return (
    <DialogPrimitive.Title
      ref={ref}
      // Without an id of its own the title keeps the one Radix references.
      {...(id === undefined ? null : { id })}
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  )
})
DialogTitle.displayName = "DialogTitle"

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, id, ...props }, ref) => {
  useDialogPart(React.useContext(DialogNamingContext)?.setDescription, id)

  return (
    <DialogPrimitive.Description
      ref={ref}
      // Without an id of its own it keeps the one Radix references.
      {...(id === undefined ? null : { id })}
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
})
DialogDescription.displayName = "DialogDescription"

/** Radix content without the default overlay — for app-specific stacking (e.g. site editor). */
const DialogContentRoot = DialogPrimitive.Content

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogContentRoot,
  DialogContainer,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
